"use strict";
// common/services/src/translation-keys.ts
Object.defineProperty(exports, "__esModule", { value: true });
exports.TranslationKeyBuilder = exports.TranslationProperty = void 0;
/**
 * Translation key builder utilities for form translations
 *
 * Key formats:
 * - Fields: form.field.{fieldName}.{property}
 * - Pages: form.page.{pageId}.{property}
 * - Sections: form.section.{sectionId}.{property}
 * - Blocks: form.block.{blockId}.{property}
 *  - Block fields: form.block.{blockId}.field.{fieldName}.{property}
 * - Buttons: form.button.{action}.label
 * - Tooltips: form.tooltip.{component}.{action}.label
 * - Messages: form.message.{category}.{type}
 * - Validations: form.validation.{rule}.label
 * - Modals: form.modal.{modalName}.{property}
 * - Instructionals: form.instructional.{step}.{property}
 */
var TranslationProperty;
(function (TranslationProperty) {
    TranslationProperty["Label"] = "label";
    TranslationProperty["Description"] = "description";
    TranslationProperty["Placeholder"] = "placeholder";
    TranslationProperty["Error"] = "error";
    TranslationProperty["Message"] = "message";
    TranslationProperty["Name"] = "name";
    TranslationProperty["Title"] = "title";
})(TranslationProperty || (exports.TranslationProperty = TranslationProperty = {}));
var TranslationKeyBuilder = /** @class */ (function () {
    function TranslationKeyBuilder() {
    }
    /**
     * Builds a field translation key
     * @param fieldName - Field name (e.g., 'applicant_name_first')
     * @param property - Property type (label, description, placeholder, error)
     */
    TranslationKeyBuilder.field = function (fieldName, property) {
        if (property === void 0) { property = TranslationProperty.Label; }
        if (!fieldName) {
            throw new Error('Field name is required');
        }
        return "form.field.".concat(fieldName, ".").concat(property);
    };
    /**
     * Builds a page translation key
     * @param pageId - Page UUID or identifier
     * @param property - Property type (name, description)
     */
    TranslationKeyBuilder.page = function (pageId, property) {
        if (property === void 0) { property = TranslationProperty.Name; }
        if (!pageId) {
            throw new Error('Page ID is required');
        }
        return "form.page.".concat(pageId, ".").concat(property);
    };
    /**
     * Builds a section translation key
     * @param sectionId - Section UUID
     * @param property - Property type (title)
     */
    TranslationKeyBuilder.section = function (sectionId, property) {
        if (property === void 0) { property = TranslationProperty.Title; }
        if (!sectionId) {
            throw new Error('Section ID is required');
        }
        return "form.section.".concat(sectionId, ".").concat(property);
    };
    /**
     * Builds a block translation key
     * @param blockId - Block UUID
     * @param property - Property type (label, name)
     */
    TranslationKeyBuilder.block = function (blockId, property) {
        if (property === void 0) { property = TranslationProperty.Label; }
        if (!blockId) {
            throw new Error('Block ID is required');
        }
        return "form.block.".concat(blockId, ".").concat(property);
    };
    // UI element methods (form-version-specific)
    /**
     * Builds a UI button translation key
     * @param action - Button action (save, cancel, next, submit, download, close, edit, publish, etc.)
     */
    TranslationKeyBuilder.button = function (action) {
        return "form.button.".concat(action, ".label");
    };
    /**
     * Builds a UI tooltip translation key
     * @param component - Component identifier (addQuestion, addTextField, agreementRequired, etc.)
     * @param action - Optional action context
     */
    TranslationKeyBuilder.toolTip = function (component, action) {
        return action
            ? "form.tooltip.".concat(component, ".").concat(action)
            : "form.tooltip.".concat(component, ".label");
    };
    /**
     * Builds a UI message translation key
     * @param category - Message category (applicationLocked, fieldDisabled, verificationFailed, etc.)
     * @param type - Message type (label, description, etc.)
     */
    TranslationKeyBuilder.message = function (category, type) {
        if (type === void 0) { type = 'label'; }
        return "ui.message.".concat(category, ".").concat(type);
    };
    /**
     * Builds a UI validation message key
     * @param rule - Validation rule (required, invalidEmail, invalidPhone, etc.)
     */
    TranslationKeyBuilder.validation = function (rule) {
        return "form.validation.".concat(rule, ".label");
    };
    /**
     * Builds a UI modal translation key
     * @param modalName - Modal name (confirmation, paymentError, etc.)
     * @param property - Property (title, message, etc.)
     */
    TranslationKeyBuilder.dialog = function (modalName, property) {
        return "form.modal.".concat(modalName, ".").concat(property);
    };
    /**
     * Builds an instructional text translation key
     * @param property - Property (text, title, etc.)
     */
    TranslationKeyBuilder.instructional = function (step, property) {
        if (property === void 0) { property = 'text'; }
        return "form.instructional.".concat(step, ".").concat(property);
    };
    /**
     * Validates a translation key format
     */
    TranslationKeyBuilder.isValidKey = function (key) {
        var patterns = [
            // Form element keys
            /^form\.field\.[\w-]+\.(label|description|placeholder|error)$/,
            /^form\.page\.[\w-]+\.(name|description)$/,
            /^form\.section\.[\w-]+\.(title)$/,
            /^form\.block\.[\w-]+\.(label|name)$/,
            /^form\.button\.[\w-]+\.label$/,
            /^form\.tooltip\.[\w-]+(\.[\w-]+)?\.(label)?$/,
            /^form\.message\.[\w-]+\.[\w-]+$/,
            /^form\.validation\.[\w-]+\.label$/,
            /^form\.modal\.[\w-]+\.(title|message|label)$/,
            /^form\.instructional\.[\w-]+\.[\w-]+$/,
        ];
        return patterns.some(function (pattern) { return pattern.test(key); });
    };
    /**
     * Parses a translation key into its components
     */
    TranslationKeyBuilder.parseKey = function (key) {
        var match = key.match(/^(form)\.(\w+)\.(.+?)\.(.+)$/);
        if (!match)
            return null;
        return {
            namespace: match[1],
            type: match[2],
            id: match[3],
            property: match[4],
        };
    };
    return TranslationKeyBuilder;
}());
exports.TranslationKeyBuilder = TranslationKeyBuilder;
